/**
 * Dossier Coverage — the record filling in, and what still blocks processing.
 *
 * Intake previously showed nine extracted values and a thirteen-item review
 * checklist, against a catalogue of seventy-six fields. An operator could see
 * neither how much of the dossier a package had actually populated, nor what
 * the loan file still lacked in order to process.
 *
 * Two questions, kept separate on purpose:
 *   - how full is the dossier (per group, per field, with provenance)
 *   - what is stopping this file (which is NOT "everything absent" — most of
 *     the catalogue is empty on a fresh record and that is normal)
 *
 * Derived values are rendered visibly distinct via the shared six-state
 * vocabulary and always carry their formula and inputs: a proposal to confirm,
 * never presented as something a document said.
 */

import { useState } from 'react'
import { formatVarianceFigure, isYearField } from '@/components/deal/dossier/format'
import { attachmentStatusLabel } from '@/components/intake/AttachmentStatusBadge'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Sigma } from 'lucide-react'

import {
  dataReview,
  dataReviewKeys,
  invalidateAfterDecision,
  type DataReviewItem,
} from '@/services/dataReview'
import {
  ingestionService,
  type DossierBlocker,
  type DossierRevision,
  type DossierSource,
  type DossierField,
  type DossierFieldStatus,
} from '@/services/ingestion'
import { FIELD_STATE_TOKENS, FieldStateBadge, type FieldState } from '@/components/fields/fieldState'
import { confirmedText, unreadModelSummary } from '@/components/fields/lineage'
import { deskDayValue, formatDeskStamp } from '@/utils/dates'

/** Server status → the platform's shared six-state display vocabulary. */
const STATE_OF: Record<DossierFieldStatus, FieldState> = {
  stated: 'stated',
  confirmed: 'confirmed',
  derived: 'derived',
  suggested: 'suggested',
  conflict: 'conflicted',
  missing: 'missing',
  in_unread_model: 'in_unread_model',
  // The shared vocabulary already has a state meaning exactly this —
  // "structurally unavailable: a human decision or wrong deal type" — so this
  // reuses it rather than inventing an eighth.
  not_derivable_from_package: 'not_applicable',
}

const BAR_OF: Record<DossierFieldStatus, string> = {
  stated: 'bg-sky-400',
  confirmed: 'bg-emerald-400',
  derived: 'bg-violet-400',
  suggested: 'bg-stone-500',
  conflict: 'bg-red-400',
  missing: 'bg-stone-700',
  in_unread_model: 'bg-indigo-400',
  not_derivable_from_package: 'bg-stone-500',
}

function displayValue(field: DossierField): string {
  const v = field.value
  if (v === null || v === undefined || v === '') {
    if (field.gapKind === 'desk_assigned' || field.gapKind === 'external') return 'needs manual'
    if (field.gapKind === 'unread_attachment') return 'in unread attachment'
    if (field.status === 'not_derivable_from_package') return 'n/a'
    // A surveyed workbook states it: say where, not "—" (UI-06).
    if (field.status === 'in_unread_model') {
      return unreadModelSummary(field.sourceModel, field.modelCell, field.candidatesInModel)
    }
    // An attached model nobody has surveyed yet may state it.
    if (field.mayBeInUnreadModel?.length) return `may be in ${field.mayBeInUnreadModel.join(', ')}`
    return '—'
  }
  if (typeof v === 'number') {
    if (isYearField(field.field)) return String(v)
    if (field.kind === 'currency') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    if (field.kind === 'percent') return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }
  return String(v)
}

/** Proportional bar of every state — the group's fill at a glance. Omitting a
 *  state here does not merely hide it: the segments are sized against `total`,
 *  so the bar silently under-fills by however many fields it refuses to name. */
function GroupBar({ counts, total }: { counts: Partial<Record<DossierFieldStatus, number>>; total: number }) {
  const order: DossierFieldStatus[] = [
    'stated', 'confirmed', 'derived', 'suggested', 'in_unread_model', 'conflict', 'missing',
    'not_derivable_from_package',
  ]
  return (
    <div className="flex h-1 w-full overflow-hidden rounded-full bg-stone-800">
      {order.map((s) =>
        counts[s] ? (
          <div key={s} className={BAR_OF[s]} style={{ width: `${((counts[s] ?? 0) / total) * 100}%` }} />
        ) : null,
      )}
    </div>
  )
}

/** Who confirmed it, when and why — a person's decision, not a document. */
function confirmedTitle(field: DossierField): string {
  const c = field.confirmation
  const when = c?.at ? ` on ${String(c.at).slice(0, 10)}` : ''
  return confirmedText(c, when)
}

function FieldRow({
  field,
  onOpenSource,
}: {
  field: DossierField
  /** Open a stored source file by name — the workbook a located value sits in. */
  onOpenSource?: (filename: string) => void
}) {
  const derived = field.status === 'derived'
  // A stated value carrying a note was read from running prose or recovered
  // from a forwarded header — the record's best answer, but not a statement of
  // record. It must not sit indistinguishable from a labelled term-sheet
  // figure, or nobody ever confirms it.
  const unconfirmed = field.status === 'stated' && Boolean(field.note)
  // Where an unread workbook states the value: a located value, drawn as one,
  // with the file openable (UI-06).
  const located = field.status === 'in_unread_model'
  const locatedTitle = located
    ? [
        field.modelLabel ? `labeled "${field.modelLabel}" in the model` : null,
        field.note ?? null,
      ].filter(Boolean).join(' — ') || undefined
    : undefined
  return (
    <div className="flex items-baseline gap-2 py-0.5">
      <span className="w-40 shrink-0 truncate text-[10px] text-stone-500" title={field.label}>
        {field.label}
        {field.required && <span className="ml-0.5 text-amber-500/70">*</span>}
      </span>
      <span
        className={`min-w-0 flex-1 truncate text-[10px] ${
          derived
            ? 'text-violet-300'
            : located
              ? 'text-indigo-300'
            : unconfirmed
              ? 'text-amber-300/90 decoration-amber-500/40 decoration-dotted underline underline-offset-2'
              : field.status === 'stated' || field.status === 'confirmed'
                ? 'text-stone-200'
                : 'text-stone-600'
        }`}
        title={
          derived
            ? `${field.formula ?? ''} — ${(field.inputs ?? [])
                .map((i) => `${i.label} ${i.value.toLocaleString()}`)
                .join(', ')}`
            : located
              ? locatedTitle
            : unconfirmed
              ? [field.note, field.provenance?.sourceDoc].filter(Boolean).join(' — ')
              : field.status === 'suggested'
                ? `Suggested — ${field.basis ?? field.note ?? 'a platform default'}. Not counted until a person confirms it.`
                : field.status === 'confirmed'
                  ? confirmedTitle(field)
                  : field.note || field.provenance?.sourceDoc || undefined
        }
        data-located-in={located && field.sourceModel ? field.sourceModel : undefined}
      >
        {field.status === 'conflict'
          ? `${field.candidates?.length ?? 0} competing values`
          : displayValue(field)}
      </span>
      {located && field.sourceModel && onOpenSource ? (
        <button
          type="button"
          onClick={() => onOpenSource(field.sourceModel as string)}
          className="shrink-0 text-[9px] text-indigo-300/90 underline decoration-dotted underline-offset-2 hover:text-indigo-200"
          title={`Open ${field.sourceModel}`}
        >
          open model
        </button>
      ) : null}
      {field.variance ? (
        // A stated metric the platform also computes and the two differ:
        // shown beside the stated value, for information (rules R3 / R4).
        // Never a choice between two outputs.
        <span
          className="shrink-0 rounded border border-amber-400/25 px-1 text-[8px] uppercase tracking-wide text-amber-300/80"
          title={`Stated ${formatVarianceFigure(field.variance.stated, field.kind)}; computed ${formatVarianceFigure(field.variance.computed, field.kind)}${field.variance.formula ? ` (${field.variance.formula})` : ''} — for information`}
        >
          variance
        </span>
      ) : null}
      <FieldStateBadge state={STATE_OF[field.status]} formula={field.formula} />
    </div>
  )
}

/** What a derivable screen input stops on: a value a calculation already
 *  answers. Bible §9 promises one click to confirm it; this row offers the
 *  click (UI-10). The decision is the Data Review row the promotion agent
 *  writes for the blocker (kind `derivable`), answered through the one decide
 *  route — Confirm binds the derived value, Reject refuses it. Until that row
 *  exists the blocker is listed and says it is waiting. */
// Unratified wording (4 Nov 2026) — Edward to sign off.
export const DERIVABLE_REJECT_REASON =
  'Derived value rejected on the intake dossier — the computed figure does not bind'

/** The Data Review row written for a derivable blocker, or null while none exists. */
export function reviewRowForBlocker(items: DataReviewItem[] | undefined, blocker: DossierBlocker): DataReviewItem | null {
  const field = blocker.derivedField ?? blocker.field
  if (!field || !items?.length) return null
  const open = items.filter((item) => item.field === field && item.kind !== 'decided' && !item.humanDecided)
  return open.find((item) => item.kind === 'derivable') ?? open[0] ?? null
}

function BlockingRow({ blocker }: { blocker: DossierBlocker }) {
  return (
    <div className="rounded border border-amber-400/25 bg-amber-400/[0.05] px-2 py-1.5">
      <div className="flex items-start gap-1.5">
        <AlertTriangle size={10} className="mt-0.5 shrink-0 text-amber-400" />
        <div className="min-w-0">
          <p className="text-[10px] font-medium text-amber-300">{blocker.why}</p>
          {blocker.fillHint && <p className="mt-0.5 text-[9px] text-stone-500">{blocker.fillHint}</p>}
        </div>
      </div>
    </div>
  )
}

function DerivableRow({
  blocker,
  item,
  busy,
  onDecide,
  onOpenReview,
}: {
  blocker: DossierBlocker
  item: DataReviewItem | null
  busy: boolean
  onDecide: (item: DataReviewItem, verb: 'confirm' | 'unavailable') => void
  onOpenReview?: (field: string) => void
}) {
  const field = blocker.derivedField ?? blocker.field ?? null
  const value = blocker.derivedValue
  return (
    <div
      className="rounded border border-violet-400/25 bg-violet-400/[0.05] px-2 py-1.5"
      data-testid="derivable-blocker"
      data-review-row={item?.id ?? undefined}
    >
      <div className="flex items-start gap-1.5">
        <Sigma size={10} className="mt-0.5 shrink-0 text-violet-400" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium text-violet-300">{blocker.why}</p>
          {blocker.fillHint && <p className="mt-0.5 text-[9px] text-stone-500">{blocker.fillHint}</p>}
          {typeof value === 'number' ? (
            <p className="mt-0.5 font-mono text-[9px] text-stone-400">
              derived value {value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </p>
          ) : null}
        </div>
        <span className="flex shrink-0 items-center gap-1">
          {item ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(item, 'confirm')}
                className="rounded border border-emerald-400/30 px-1.5 py-px text-[9px] text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-50"
                title="Bind the derived value — recorded under your name"
              >
                {busy ? <Loader2 size={9} className="inline animate-spin" /> : 'Confirm'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(item, 'unavailable')}
                className="rounded border border-stone-700 px-1.5 py-px text-[9px] text-stone-300 hover:bg-stone-800 disabled:opacity-50"
                title="Refuse the derived value — recorded under your name"
              >
                Reject
              </button>
            </>
          ) : field && onOpenReview ? (
            <button
              type="button"
              onClick={() => onOpenReview(field)}
              className="rounded border border-amber-400/25 px-1.5 py-px text-[9px] text-amber-200 hover:bg-amber-400/10"
              title="Decide this input in the Data Intelligence Review"
            >
              Decide in review
            </button>
          ) : (
            <span className="text-[9px] text-stone-600" title="The Data Review row for this input has not been written yet">
              awaiting its review row
            </span>
          )}
        </span>
      </div>
    </div>
  )
}

export default function DossierCoveragePanel({
  ingestionId,
  onOpenSource,
  onOpenReview,
}: {
  ingestionId: string
  /** Open one of the record's stored files by name (the source viewer). */
  onOpenSource?: (filename: string) => void
  /** Open the Data Intelligence Review on this record, at the row for a field. */
  onOpenReview?: (field: string) => void
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [decisionError, setDecisionError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['ingestion-dossier', ingestionId],
    queryFn: () => ingestionService.getDossier(ingestionId),
  })
  // The Data Review rows behind the derivable blockers (UI-10) — asked for
  // only when there is one to decide.
  const derivableCount = data?.blockers?.derivable ?? 0
  const review = useQuery({
    queryKey: dataReviewKeys.record(ingestionId),
    queryFn: () => dataReview.record(ingestionId),
    enabled: derivableCount > 0,
    retry: false,
  })
  const decide = useMutation({
    mutationFn: (input: { item: DataReviewItem; verb: 'confirm' | 'unavailable' }) =>
      dataReview.decide({
        itemId: input.item.id,
        verb: input.verb,
        reason: input.verb === 'unavailable' ? DERIVABLE_REJECT_REASON : undefined,
      }),
    onSuccess: async () => {
      setDecisionError(null)
      await invalidateAfterDecision(queryClient, review.data?.loanId ?? null)
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setDecisionError(typeof detail === 'string' ? detail : 'The decision was not recorded. Please retry.')
    },
  })

  if (isLoading) {
    return (
      <div className="panel">
        <div className="panel-body flex items-center gap-2 text-[10px] text-stone-500">
          <Loader2 size={11} className="animate-spin" /> Reading dossier coverage…
        </div>
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="panel">
        <div className="panel-body text-[10px] text-stone-500">
          Dossier coverage unavailable for this record.
        </div>
      </div>
    )
  }

  const { groups, counts, populated, total, blockers, sources, revisions, refused } = data
  const packageTotal = data.packageTotal ?? total
  const needsManual = data.needsManual ?? 0
  const pct = packageTotal ? Math.round((populated / packageTotal) * 100) : 0

  return (
    <div className="space-y-3">
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Deal Dossier</span>
          <span className="text-[10px] text-stone-500">
            {populated}/{packageTotal} from package · {pct}%
            {needsManual > 0 && ` · ${needsManual} need manual`}
          </span>
        </div>
        <div className="panel-body space-y-2">
          <GroupBar counts={counts} total={total} />

          {/* Columns the record OFFERED and the catalogue turned away. A field
              that reads absent because its value was inadmissible is a
              different finding from one nothing stated — and saying so is the
              whole point of refusing rather than silently storing. */}
          {refused && refused.length > 0 && (
            <div className="flex items-start gap-1.5 rounded border border-amber-400/20 bg-amber-400/[0.04] px-2 py-1.5">
              <AlertTriangle className="mt-px h-3 w-3 flex-shrink-0 text-amber-300" />
              <div className="min-w-0 text-[10px] leading-relaxed text-stone-400">
                <span className="text-amber-200">
                  {refused.length} value{refused.length === 1 ? '' : 's'} the record
                  stated could not be recorded
                </span>{' '}
                — each was inadmissible for its column, so the field reads absent
                rather than holding a mis-parse:
                <ul className="mt-0.5 space-y-0.5">
                  {refused.map((r) => (
                    <li key={r} className="truncate text-stone-500" title={r}>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {(
              [
                'stated', 'confirmed', 'derived', 'suggested', 'in_unread_model', 'conflict', 'missing',
                'not_derivable_from_package',
              ] as DossierFieldStatus[]
            ).map((s) =>
              counts[s] ? (
                <span key={s} className="flex items-center gap-1 text-[9px] text-stone-500">
                  <span className={`h-1.5 w-1.5 rounded-full ${FIELD_STATE_TOKENS[STATE_OF[s]].dot}`} />
                  {counts[s]} {FIELD_STATE_TOKENS[STATE_OF[s]].label.toLowerCase()}
                </span>
              ) : null,
            )}
          </div>

          <div className="space-y-1 pt-1">
            {groups.map((g) => {
              const expanded = open[g.group] ?? false
              return (
                <div key={g.group} className="rounded border border-stone-800/80">
                  <button
                    type="button"
                    onClick={() => setOpen((o) => ({ ...o, [g.group]: !expanded }))}
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-stone-800/30"
                  >
                    {expanded ? (
                      <ChevronDown size={10} className="shrink-0 text-stone-500" />
                    ) : (
                      <ChevronRight size={10} className="shrink-0 text-stone-500" />
                    )}
                    <span className="w-36 shrink-0 truncate text-[10px] font-medium text-stone-300">
                      {g.group}
                    </span>
                    <span className="min-w-0 flex-1">
                      <GroupBar counts={g.counts} total={g.total} />
                    </span>
                    <span className="w-12 shrink-0 text-right text-[10px] tabular-nums text-stone-500">
                      {g.populated}/{g.total}
                    </span>
                  </button>
                  {expanded && (
                    <div className="border-t border-stone-800/80 px-2 py-1">
                      {g.fields.map((f) => (
                        <FieldRow key={f.field} field={f} onOpenSource={onOpenSource} />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <p className="text-[9px] leading-snug text-stone-600">{data.basis}</p>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">To process this file</span>
          <span className="text-[10px] text-stone-500">
            {blockers.blocking} blocking
            {blockers.derivable > 0 && ` · ${blockers.derivable} confirmable`}
          </span>
        </div>
        <div className="panel-body space-y-1.5">
          {blockers.items.length === 0 ? (
            <p className="text-[10px] text-emerald-300">
              Nothing outstanding — this file has what it needs to process.
            </p>
          ) : (
            blockers.items.map((b, i) =>
              b.severity === 'derivable' ? (
                <DerivableRow
                  key={`${b.kind}-${b.field ?? ''}-${i}`}
                  blocker={b}
                  item={reviewRowForBlocker(review.data?.items, b)}
                  busy={decide.isPending && decide.variables?.item.field === (b.derivedField ?? b.field)}
                  onDecide={(item, verb) => decide.mutate({ item, verb })}
                  onOpenReview={onOpenReview}
                />
              ) : (
                <BlockingRow key={`${b.kind}-${b.field ?? ''}-${i}`} blocker={b} />
              ),
            )
          )}
          {decisionError ? (
            <p className="flex items-center gap-1 text-[9px] text-red-300">
              <AlertTriangle size={9} /> {decisionError}
            </p>
          ) : null}
          <p className="text-[9px] leading-snug text-stone-600">
            Only what a downstream step actually stopped on. An empty catalog field is
            normal on a fresh record and is not listed here.
            {derivableCount > 0 ? ' A confirmable input is answered by a calculation: Confirm binds it, Reject refuses it.' : ''}
          </p>
        </div>
      </div>

      {sources && sources.length > 0 && (
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Sources</span>
            <span className="text-[10px] text-stone-500">
              {sources.length} {sources.length === 1 ? 'message' : 'messages'}
              {revisions && revisions.length > 0 && ` · ${revisions.length} revised`}
            </span>
          </div>
          <div className="panel-body space-y-1.5">
            {/* Oldest first. A reply revises SOME datapoints and leaves the
                rest standing, so the dossier is the thread's answer rather
                than the last message's — this is the order that produced it. */}
            {sources.map((s) => (
              <SourceRow key={s.ingestionId} source={s} />
            ))}
            {revisions && revisions.length > 0 && (
              <div className="mt-2 border-t border-stone-800 pt-2">
                <p className="mb-1 text-[10px] font-medium text-stone-400">Revised since first stated</p>
                {revisions.map((r) => (
                  <RevisionRow key={r.field} revision={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SourceRow({ source }: { source: DossierSource }) {
  const stamp = formatDeskStamp(source.receivedAt)
  return (
    <div
      className={`rounded border px-2 py-1.5 ${
        source.isCurrent ? 'border-amber-400/30 bg-amber-400/5' : 'border-stone-800 bg-stone-900/40'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="w-4 shrink-0 text-[10px] text-stone-600">{source.position}.</span>
        <span className="shrink-0 font-mono text-[10px] text-stone-400">
          {/* The desk's clock (New York), not UTC's and not the viewer's own: a
              9:30pm New York email read as 01:30 the next day (N2), and
              `getHours()` read it in whatever zone the browser was in. */}
          {stamp && /^\d{4}-\d{2}-\d{2}/.test(stamp) ? stamp : 'undated'}
        </span>
        <span className="min-w-0 flex-1 truncate text-[10px] text-stone-200" title={source.subject ?? ''}>
          {source.subject || '(no subject)'}
        </span>
      </div>
      <div className="ml-6 text-[9px] leading-snug text-stone-500">
        {source.fromEmail || 'unknown sender'}
        {source.to.length > 0 && <> → {source.to.join(', ')}</>}
        {source.cc.length > 0 && <> · cc {source.cc.join(', ')}</>}
      </div>
      {source.attachments.length > 0 && (
        <div className="ml-6 mt-0.5 flex flex-wrap gap-1">
          {source.attachments.map((a, i) => (
            <span
              key={`${a.filename}-${i}`}
              className="rounded border border-stone-700 px-1 py-px text-[9px] text-stone-400"
              title={a.status ? attachmentStatusLabel(a.status).label : undefined}
            >
              {a.filename}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function RevisionRow({ revision }: { revision: DossierRevision }) {
  // The desk's day, not the UTC stamp's (N2) and not the viewer's own zone's.
  const stamp = (iso: string | null) => deskDayValue(iso) ?? 'undated'
  return (
    <div className="py-0.5 text-[10px]">
      <span className="text-stone-400">{revision.field}</span>{' '}
      <span className="text-stone-200">{String(revision.value)}</span>{' '}
      <span className="text-stone-600">as of {stamp(revision.receivedAt)}</span>
      {revision.superseded.map((s, i) => (
        <div key={i} className="ml-3 text-[9px] text-stone-600">
          was <span className="line-through">{String(s.value)}</span> · {stamp(s.receivedAt)}
          {s.quoted && ' (quoted)'}
        </div>
      ))}
    </div>
  )
}
